import { Box, Button, Heading, useBreakpointValue } from 'native-base';
import React, { useEffect, useMemo, useState } from 'react';
import { Linking, Platform } from 'react-native';

import HTML from 'app/components/HTML';
import Map from 'app/components/Map';
import { MapMarker, MapZoomLevel } from 'app/components/Map/Map.types';
import { AttractionContent } from 'app/hooks/useAttraction';
import useGuest from 'app/hooks/useGuest';
import { useI18n } from 'app/providers/I18nProvider';
import Analytics, { Events } from 'app/services/Analytics';
import { getLocationAddressLink, getLocations } from 'app/services/AttractionHelper';
import Logger from 'app/services/Logger';

export type AttractionDetailsMapProps = {
  attraction: NonNullable<AttractionContent>;
  productContentKey: string;
};

export default function AttractionMap({
  attraction,
  productContentKey,
}: AttractionDetailsMapProps) {
  const { t } = useI18n();
  const mapRatio = useBreakpointValue([4 / 3, 5 / 3, 5 / 2]);
  const [showReset, setShowReset] = useState(false);
  const [reset, setReset] = useState(false);
  const { activeEntitlement, hasReservations } = useGuest();

  const locations = useMemo(() => {
    return getLocations(attraction, hasReservations, activeEntitlement?.reservations);
  }, [attraction, activeEntitlement?.reservations, hasReservations]);

  const showNames = locations.length > 1;
  const markers: MapMarker[] = useMemo(() => {
    return locations.map((loc) => ({
      isDisabled: loc.isDisabled,
      title: loc.name || '',
      latitude: loc.geolocation?.latitude || 0,
      longitude: loc.geolocation?.longitude || 0,
    }));
  }, [locations]);

  const initialZoom: MapZoomLevel = useMemo(() => {
    const mutiLocation = markers.length > 1;

    return Platform.select({
      android: mutiLocation ? 11 : 13,
      default: mutiLocation ? 14 : 15,
    });
  }, [markers]);

  const openDirections = async (location: Single<AttractionContent['locations']>) => {
    try {
      // this can fail if iOS user removed the Apple Maps app since Linking.canOpenURL() still returns true
      await Linking.openURL(await getLocationAddressLink(location));
    } catch (err) {
      Logger.info('[AttractionMap] failed to open attraction directions', { err });
      // so we fallback to Google Maps on web
      Linking.openURL(await getLocationAddressLink(location, 'google'));
    }
  };

  // we show the reset button when the user moves the
  // map so this also seems like a good place to track
  // interactions
  useEffect(() => {
    if (showReset) {
      Analytics.trackEvent(Events.AttractionMapControls, {
        attractionKey: attraction.key,
        productKey: productContentKey,
      });
    }
  }, [attraction.key, productContentKey, showReset]);

  return (
    <Box testID="AttractionDetailsMap">
      <Map
        markers={markers}
        ratio={mapRatio}
        reset={reset}
        scrollEnabled
        zoom={initialZoom}
        onPan={() => {
          setShowReset(true);
        }}
        onReset={() => {
          setReset(false);
          setShowReset(false);
        }}
      />

      {locations.map((loc) => (
        <Button
          key={loc.name}
          _text={{
            // fixes text overflow on web due to extra parent flexbox
            _web: { maxWidth: '300px' },
          }}
          isDisabled={loc.isDisabled}
          mt="4"
          onPress={() => {
            openDirections(loc);
            Analytics.trackEvent(Events.AttractionDirections, {
              attractionKey: attraction.key,
              productKey: productContentKey,
            });
          }}
        >
          {t('gen_directions') + (showNames ? `: ${loc.name}` : '')}
        </Button>
      ))}
      <Button
        alignSelf="center"
        mt="2"
        opacity={showReset ? 1 : 0}
        variant="link"
        onPress={() => setReset(true)}
      >
        {t('gen_reset')}
      </Button>
      {!!attraction.parking && (
        <Box>
          <Heading fontSize="sm" mb="2">
            {t('gen_attraction_details_parking_title')}
          </Heading>
          <HTML html={attraction.parking} />
        </Box>
      )}
    </Box>
  );
}
